import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import GoogleIcon from "@/components/GoogleIcon";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = `Create account — ${BRAND.name}`; }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register({ name: form.name.trim(), email: form.email.trim(), password: form.password });
    setLoading(false);
    if (res.ok) { toast.success("Account created"); nav("/dashboard", { replace: true }); }
    else { toast.error(res.error || "Registration failed"); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="register-back-home" className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-brand font-semibold">
            ← {BRAND.short}
          </Link>
          <h1 className="font-heading font-bold text-4xl mt-6 leading-tight">Open your claim file</h1>
          <p className="text-sm text-slate-600 mt-2">Create a free client account. Upload documents in minutes.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Full name</Label>
              <Input required value={form.name} onChange={setField("name")} data-testid="register-name-input"
                className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Email</Label>
              <Input required type="email" value={form.email} onChange={setField("email")} data-testid="register-email-input"
                className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Password (min 6)</Label>
              <Input required minLength={6} type="password" value={form.password} onChange={setField("password")} data-testid="register-password-input"
                className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11" />
            </div>

            <button type="submit" disabled={loading} data-testid="register-submit-btn" className="btn-emerald w-full text-sm">
              {loading ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={startGoogleAuth}
            data-testid="register-google-btn"
            className="mt-4 w-full h-11 inline-flex items-center justify-center gap-3 rounded-full border-2 border-slate-200 hover:border-navy transition-colors font-semibold text-sm text-slate-800 bg-white"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="mt-8 border-t border-slate-200 pt-6 text-sm text-slate-600">
            Already a client?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-emerald-brand font-semibold hover:underline">
              Sign in →
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F9D58] via-[#0c8a4d] to-[#0A2540]" />
        <div className="absolute inset-0 bg-dot-grid opacity-15" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="" className="h-12 w-12 object-contain bg-white rounded-xl p-1.5" />
            <div>
              <div className="font-heading font-bold text-lg text-white">Accurate Claims Services</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/90 font-semibold">{BRAND.subline}</div>
            </div>
          </div>
          <div>
            <h2 className="font-heading font-bold text-4xl leading-tight max-w-md text-white drop-shadow">
              Your file. Your record. <span className="text-gold drop-shadow">Always with you.</span>
            </h2>
            <ul className="mt-6 space-y-3 max-w-md">
              {["Free first consultation", "Secure encrypted document storage", "Real-time status with notifications", "Independent — we never sell insurance"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle2 className="w-5 h-5 text-gold shrink-0" /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
